/**
 * Custom Scroll Animation Utility
 * Works independently of AOS library
 */

// Animation configurations
const animations = {
  'fade-up': {
    initial: { opacity: 0, transform: 'translateY(30px)' },
    animate: { opacity: 1, transform: 'translateY(0)' }
  },
  'fade-down': {
    initial: { opacity: 0, transform: 'translateY(-30px)' },
    animate: { opacity: 1, transform: 'translateY(0)' }
  },
  'fade-left': {
    initial: { opacity: 0, transform: 'translateX(30px)' },
    animate: { opacity: 1, transform: 'translateX(0)' }
  },
  'fade-right': {
    initial: { opacity: 0, transform: 'translateX(-30px)' },
    animate: { opacity: 1, transform: 'translateX(0)' }
  },
  'fade': {
    initial: { opacity: 0 },
    animate: { opacity: 1 }
  },
  'zoom-in': {
    initial: { opacity: 0, transform: 'scale(0.8)' },
    animate: { opacity: 1, transform: 'scale(1)' }
  },
  'zoom-out': {
    initial: { opacity: 0, transform: 'scale(1.2)' },
    animate: { opacity: 1, transform: 'scale(1)' }
  },
  'flip-left': {
    initial: { opacity: 0, transform: 'rotateY(-90deg)' },
    animate: { opacity: 1, transform: 'rotateY(0)' }
  },
  'flip-right': {
    initial: { opacity: 0, transform: 'rotateY(90deg)' },
    animate: { opacity: 1, transform: 'rotateY(0)' }
  }
};

let observer = null;

/**
 * Apply animation to an element
 */
const animateElement = (element, animationType = 'fade-up', duration = 1000, delay = 0) => {
  const config = animations[animationType] || animations['fade-up'];
  
  // Set initial state
  element.style.opacity = '0';
  element.style.transition = `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`;
  element.style.willChange = 'opacity, transform';
  
  if (config.initial.transform) {
    element.style.transform = config.initial.transform;
  }

  // Apply animation after delay
  setTimeout(() => {
    element.style.opacity = config.animate.opacity || '1';
    if (config.animate.transform) {
      element.style.transform = config.animate.transform;
    }
    element.classList.add('aos-animate');
    
    // Clean up willChange after animation
    setTimeout(() => {
      element.style.willChange = 'auto';
    }, duration);
  }, delay);
};

/**
 * Initialize scroll animations
 */
export const initScrollAnimation = () => {
  // Clean up existing observer
  if (observer) {
    observer.disconnect();
  }

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  };

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !entry.target.classList.contains('aos-animate')) {
        const element = entry.target;
        const animationType = element.getAttribute('data-aos') || 'fade-up';
        const duration = parseInt(element.getAttribute('data-aos-duration')) || 1000;
        const delay = parseInt(element.getAttribute('data-aos-delay')) || 0;

        animateElement(element, animationType, duration, delay);
        
        // Stop observing this element once animated
        observer.unobserve(element);
      }
    });
  }, options);

  // Observe all elements with data-aos attribute
  const elements = document.querySelectorAll('[data-aos]');
  elements.forEach((el) => {
    // Reset and observe if not already animated
    if (!el.classList.contains('aos-animate')) {
      // Reset styles
      el.style.opacity = '';
      el.style.transform = '';
      el.style.transition = '';
      observer.observe(el);
    }
  });
};

/**
 * Refresh animations (for dynamic content)
 */
export const refreshScrollAnimation = () => {
  setTimeout(() => {
    initScrollAnimation();
  }, 100);
};

/**
 * Cleanup observer
 */
export const destroyScrollAnimation = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
};

/**
 * Initialize with mutation observer for dynamic content
 */
export const initScrollAnimationWithMutationObserver = () => {
  initScrollAnimation();

  // Watch for DOM changes
  const mutationObserver = new MutationObserver(() => {
    refreshScrollAnimation();
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  return () => {
    destroyScrollAnimation();
    mutationObserver.disconnect();
  };
};

